import * as React from 'react';
import { rem } from 'polished';
import Img from 'gatsby-image';
import { graphql } from 'gatsby';

import { styled } from '../../themeContext';
import { ReactComponent as ArrowIconSvg } from './devsistersGamesItem/arrow.svg';

export type DevsistersGamesItemStatus = 'pre' | 'live' | 'upcoming';
interface DevsistersGamesItemProps {
  game: GatsbyTypes.DevsistersGamesGameItemFragment;
  onClick?: () => void;
  upcomingLabel: string;
  preLabel: string;
}

const DevsistersGamesItemContainer = styled.a(props => ({
  padding: `${rem(15)}`,
  display: 'flex',
  textDecoration: 'none',
  borderTop: '1px solid #45464c',
  [props.theme.media['sm']]: {
    padding: `${rem(20)}`,
  }
}));

const DevsistersGameItemImage = styled(Img)(props => ({
  marginRight: rem(15),
  width: rem(100),
  height: rem(100),
  borderRadius: rem(10),
  [props.theme.media['lg']]: {
    width: rem(110),
    height: rem(110),
  }
}));

const DevsistersGamesItemTitleAndDescription = styled.div({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
});

const DevsistersGamesItemTitle = styled.h1(props => ({
  margin: 0,
  fontSize: rem(16),
  fontWeight: 700,
  color: '#fff',
  lineHeight: 1.2,
  '&:lang(ja)': {
    fontSize: rem(15),
  },
  [props.theme.media['md']]: {
    fontSize: rem(20),
    '&:lang(ja)': {
      fontSize: rem(18),
    },
  },
}));

const DevsistersGamesItemDescription = styled.p(props => ({
  margin: 0,
  fontSize: rem(15),
  color: 'rgb(174, 174, 179)',
  '&:lang(ja)': {
    fontSize: rem(14),
  },
  [props.theme.media['sm']]: {
    whiteSpace: 'pre-line',
  },
  [props.theme.media['md']]: {
    '&:lang(ja)': {
      fontSize: rem(15),
    },
  },
}));

const DevsistersGamesItemPreRegistrationBadge = styled.span(props => ({
  display: 'inline-block',
  fontSize: rem(13),
  padding: `${rem(4)} ${rem(12)}`,
  borderRadius: rem(20),
  fontWeight: 700,
  letterSpacing: '-0.01em',
  color: 'rgb(25, 29, 36)',
  background: 'rgb(255, 215, 30)',
  [props.theme.media['md']]: {
    fontSize: rem(13),
  },
}));

const DevsistersGamesItemUpcomingBadge = styled(DevsistersGamesItemPreRegistrationBadge)({
  background: '#EF8855',
});

const DevsistersGamesItemBadgeArrow = styled(ArrowIconSvg)({
  width: rem(10),
  height: rem(11),
  marginLeft: rem(8),
});

const DevsistersGamesItem: React.FC<DevsistersGamesItemProps> = ({
  game,
  onClick,
  preLabel,
  upcomingLabel,
}) => {
  return (
    <DevsistersGamesItemContainer href={game.url} onClick={onClick}>
      {game.image.fixed && <DevsistersGameItemImage fixed={game.image.fixed} />}
      <DevsistersGamesItemTitleAndDescription>
        <div>
          <DevsistersGamesItemTitle>{game.title}</DevsistersGamesItemTitle>
          <DevsistersGamesItemDescription>{game.description}</DevsistersGamesItemDescription>
        </div>
        {game.status === 'pre' && (
          <div>
            <DevsistersGamesItemPreRegistrationBadge>
              {preLabel}
              <DevsistersGamesItemBadgeArrow />
            </DevsistersGamesItemPreRegistrationBadge>
          </div>
        )}
        {game.status === 'upcoming' && (
          <div>
            <DevsistersGamesItemUpcomingBadge>
              {upcomingLabel}
              <DevsistersGamesItemBadgeArrow />
            </DevsistersGamesItemUpcomingBadge>
          </div>
        )}
      </DevsistersGamesItemTitleAndDescription>
    </DevsistersGamesItemContainer>
  );
};

export default DevsistersGamesItem

export const fragments = graphql`
  fragment DevsistersGamesGameItem on OtherGameItem {
      id
      url
      title
      status
      image {
        fixed(width: 110, height: 110, quality: 80) {
          ...GatsbyImageSharpFixed_withWebp_tracedSVG
        }
      }
      description
  }
`;
